import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, CreditCard, Receipt, Wallet, ChevronRight, AlertCircle, ChevronDown } from 'lucide-react';
import { createPayment } from '../lib/payments';
import { supabase } from '../lib/supabaseClient';
// Using proven invoice payment link system for in-person payments

interface RecordPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  bookingId: string;
  businessId: string;
  bookingTotal: number;
  amountPaid: number;
}

const manualPaymentMethods = [
  {
    id: 'credit_card',
    name: 'Record Credit / Debit',
    icon: <CreditCard className="h-5 w-5 text-purple-500" />,
    description: 'Record credit or debit card payment'
  },
  {
    id: 'bank_transfer',
    name: 'Record Bank Transfer',
    icon: <Receipt className="h-5 w-5 text-green-500" />,
    description: 'Record direct bank transfer payment'
  },
  {
    id: 'cash',
    name: 'Record Cash',
    icon: <Wallet className="h-5 w-5 text-yellow-500" />,
    description: 'Record cash payment'
  }
];

const stripePaymentMethod = {
  id: 'stripe',
  name: 'Take Stripe Payment',
  icon: <img src="/Stripe (2).png" alt="Stripe" className="h-10 w-10" />,
  description: 'Process payment through Stripe'
};

export default function RecordPaymentModal({
  isOpen,
  onClose,
  onSuccess,
  bookingId,
  businessId,
  bookingTotal,
  amountPaid
}: RecordPaymentModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedMethod, setSelectedMethod] = useState(manualPaymentMethods[0]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [paymentType, setPaymentType] = useState<'manual' | 'stripe'>('manual');
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [stripeConnectEnabled, setStripeConnectEnabled] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      // Set amount to full remaining balance - only full payments are allowed
      const remainingBalance = Math.round((bookingTotal - amountPaid) * 100) / 100;
      setAmount(remainingBalance.toFixed(2));
      
      // Reset form state
      setSelectedMethod(manualPaymentMethods[0]);
      setPaymentType('manual');
      setIsDropdownOpen(false);
      setReference('');
      setNotes('');
      setError(null);
      
      // Check if Stripe Connect is enabled (simple check like invoice system)
      checkStripeConnectStatus();
    }
  }, [isOpen, bookingTotal, amountPaid, businessId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setIsDropdownOpen(false);
    };

    if (isDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isDropdownOpen]);

  const checkStripeConnectStatus = async () => {
    try {
      const { data: business, error } = await supabase
        .from('businesses')
        .select('stripe_connect_account_id, stripe_connect_charges_enabled')
        .eq('id', businessId)
        .single();

      if (error || !business) {
        console.error('❌ Failed to fetch business Stripe Connect status:', error);
        setStripeConnectEnabled(false);
        return;
      }

      const isEnabled = business.stripe_connect_account_id && business.stripe_connect_charges_enabled;
      setStripeConnectEnabled(isEnabled);
      
      if (isEnabled) {
        console.log('✅ Stripe Connect enabled for business');
      } else {
        console.log('❌ Stripe Connect not properly configured');
      }
    } catch (error) {
      console.error('❌ Error checking Stripe Connect status:', error);
      setStripeConnectEnabled(false);
    }
  };

  // Simple check for Stripe Connect - no complex initialization needed

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setError(null);
      setSelectedMethod(paymentMethods[0]);
      setAmount('');
      setReference('');
      setNotes('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!bookingId) return;

    // For Stripe payments, the StripePaymentForm handles the submission
    if (selectedMethod.id === 'stripe') {
      return;
    }

    try {
      setLoading(true);

      console.log('🎯 [UI DEBUG] User clicked "Record Payment" button');
      console.log('🎯 [UI DEBUG] Payment form data:', {
        bookingId,
        amount,
        method: selectedMethod.id,
        status: 'completed',
        reference,
        notes
      });

      const payment = await createPayment({
        business_id: businessId,
        booking_id: bookingId,
        amount: parseFloat(amount),
        method: selectedMethod.id as any,
        status: 'completed',
        reference,
        notes
      });

      console.log('✅ [UI DEBUG] Payment creation completed, result:', payment);
      console.log('✅ [UI DEBUG] PDF URL in result:', payment.pdf_url);

      onSuccess();
      onClose();
    } catch (error) {
      console.error('❌ [UI DEBUG] Payment creation failed:', error);
      setError(error instanceof Error ? error.message : 'Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  const handleStripePaymentLink = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    try {
      setLoading(true);
      
      console.log('🔗 Creating payment record and generating payment link');

      // First create a payment record in the database
      const payment = await createPayment({
        business_id: businessId,
        booking_id: bookingId,
        amount: parseFloat(amount),
        method: 'credit_card',
        status: 'pending', // Will be updated to 'completed' when paid
        reference: '',
        notes: notes || 'In-person payment'
      });

      console.log('✅ Payment record created:', payment.id);

      // Generate payment link using the existing invoice system
      const { data: linkData, error: linkError } = await supabase.functions.invoke('generate-invoice-payment-link', {
        body: {
          paymentId: payment.id
        }
      });

      if (linkError) {
        throw new Error(linkError.message);
      }

      console.log('✅ Payment link generated:', linkData.paymentUrl);

      // Open payment link in new tab
      window.open(linkData.paymentUrl, '_blank');

      // Show success message and close modal
      onSuccess();
      onClose();

    } catch (error) {
      console.error('❌ Failed to generate payment link:', error);
      setError(error instanceof Error ? error.message : 'Failed to generate payment link');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  const modalContent = (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[100010]`}
      onClick={onClose}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out shadow-2xl ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          {/* Header - Clean Modern Design */}
          <div className="bg-gray-900 border-b border-gray-800">
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center space-x-3">
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
                <h2 className="text-xl font-semibold text-white">
                  Record Payment
                </h2>
              </div>
            </div>
          </div>

          <form 
            onSubmit={handleSubmit} 
            className="flex-1 overflow-y-auto bg-gray-50"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div className="space-y-0">
              {error && (
                <div className="bg-red-50 border-b border-red-200 text-red-700 px-6 py-4 text-sm">
                  <div className="flex items-center">
                    <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                    {error}
                  </div>
                </div>
              )}

              {/* Payment Summary */}
              <div className="bg-white border-b border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Summary</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <div className="text-sm text-gray-500 font-medium">Total Amount</div>
                    <div className="mt-1 text-lg font-bold text-gray-900">
                      ${(Math.round(bookingTotal * 100) / 100).toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 font-medium">Amount Paid</div>
                    <div className="mt-1 text-lg font-bold text-green-600">
                      ${(Math.round(amountPaid * 100) / 100).toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 font-medium">Balance Due</div>
                    <div className="mt-1 text-lg font-bold text-red-600">
                      ${(Math.round((bookingTotal - amountPaid) * 100) / 100).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div className="bg-white border-b border-gray-200 p-6">
                <label className="block text-lg font-semibold text-gray-900 mb-4">
                  Payment Method
                </label>
                <div className="space-y-3">
                  {/* Manual Payment Methods Dropdown */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => {
                        if (paymentType !== 'manual') {
                          setPaymentType('manual');
                          setSelectedMethod(manualPaymentMethods[0]);
                        }
                        setIsDropdownOpen(!isDropdownOpen);
                      }}
                      className={`w-full flex items-center p-4 border-2 rounded-lg transition-colors ${
                        paymentType === 'manual'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
                      }`}
                    >
                      <div className={`h-12 w-12 rounded-lg flex items-center justify-center mr-4 ${
                        paymentType === 'manual' 
                          ? 'bg-blue-100' 
                          : 'bg-gray-100'
                      }`}>
                        {selectedMethod.icon}
                      </div>
                      <div className="flex-1 text-left">
                        <div className="text-sm font-semibold text-gray-900">{selectedMethod.name}</div>
                        <div className="text-sm text-gray-600">{selectedMethod.description}</div>
                      </div>
                      <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${
                        isDropdownOpen ? 'rotate-180' : ''
                      }`} />
                    </button>

                    {/* Dropdown Menu */}
                    {isDropdownOpen && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                        {manualPaymentMethods.map((method) => (
                          <button
                            key={method.id}
                            type="button"
                            onClick={() => {
                              setSelectedMethod(method);
                              setPaymentType('manual');
                              setIsDropdownOpen(false);
                            }}
                            className={`w-full flex items-center p-3 hover:bg-gray-50 transition-colors ${
                              selectedMethod.id === method.id ? 'bg-blue-50' : ''
                            } ${method.id === manualPaymentMethods[0].id ? 'rounded-t-lg' : ''} ${
                              method.id === manualPaymentMethods[manualPaymentMethods.length - 1].id ? 'rounded-b-lg' : ''
                            }`}
                          >
                            <div className="h-8 w-8 rounded-lg flex items-center justify-center mr-3 bg-gray-100">
                              {method.icon}
                            </div>
                            <div className="flex-1 text-left">
                              <div className="text-sm font-medium text-gray-900">{method.name}</div>
                              <div className="text-xs text-gray-600">{method.description}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Stripe Payment Method */}
                  {stripeConnectEnabled && (
                    <button
                      type="button"
                      onClick={() => {
                        setPaymentType('stripe');
                        setIsDropdownOpen(false);
                      }}
                      className={`w-full flex items-center p-4 border-2 rounded-lg transition-colors ${
                        paymentType === 'stripe'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
                      }`}
                    >
                      <div className={`h-12 w-12 rounded-lg flex items-center justify-center mr-4 ${
                        paymentType === 'stripe' 
                          ? 'bg-blue-100' 
                          : 'bg-gray-100'
                      }`}>
                        {stripePaymentMethod.icon}
                      </div>
                      <div className="flex-1 text-left">
                        <div className="text-sm font-semibold text-gray-900">{stripePaymentMethod.name}</div>
                        <div className="text-sm text-gray-600">{stripePaymentMethod.description}</div>
                      </div>
                      {paymentType === 'stripe' && (
                        <ChevronRight className="h-5 w-5 text-blue-500" />
                      )}
                    </button>
                  )}
                </div>
              </div>

              {/* Payment Details */}
              <div className="bg-white border-b border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Details</h3>

                {paymentType === 'stripe' ? (
                  <div className="space-y-4">
                    {/* Amount field for Stripe */}
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-2">
                        Amount *
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 text-sm font-medium">$</span>
                        </div>
                        <input
                          type="text"
                          readOnly
                          value={amount}
                          className="w-full pl-8 pr-4 py-3 text-base bg-gray-50 border border-gray-300 rounded-2xl cursor-not-allowed"
                          placeholder="0.00"
                          style={{ 
                            minHeight: '48px',
                            fontSize: '16px'
                          }}
                        />
                      </div>
                      <div className="mt-2">
                        <p className="text-xs text-gray-600">
                          Full payment amount only - partial payments are not allowed
                        </p>
                      </div>
                    </div>

                    {/* Notes field for Stripe */}
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-2">
                        Notes
                      </label>
                      <textarea
                        rows={3}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        className="w-full px-4 py-3 text-base bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                        placeholder="Add any additional notes about this payment..."
                        style={{ fontSize: '16px' }}
                      />
                    </div>

                    {/* Stripe Payment Link Button */}
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-purple-700">Amount to charge:</span>
                        <span className="text-lg font-bold text-purple-900">${amount}</span>
                      </div>
                      <p className="text-xs text-purple-600 mt-1">
                        Payment will be processed through your connected Stripe account
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={handleStripePaymentLink}
                      disabled={loading || !amount || parseFloat(amount) <= 0}
                      className={`w-full px-6 py-4 text-base font-bold text-white rounded-2xl transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-lg touch-manipulation ${
                        (!loading && amount && parseFloat(amount) > 0)
                          ? 'bg-purple-600 hover:bg-purple-700 shadow-purple-500/20' 
                          : 'bg-gray-300 cursor-not-allowed'
                      } disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
                      style={{ minHeight: '52px' }}
                    >
                      {loading ? (
                        <div className="flex items-center justify-center">
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                          Generating Payment Link...
                        </div>
                      ) : (
                        `Generate Stripe Payment Link - $${amount}`
                      )}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-2">
                        Amount *
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 text-sm font-medium">$</span>
                        </div>
                        <input
                          type="text"
                          readOnly
                          value={amount}
                          className="w-full pl-8 pr-4 py-3 text-base bg-gray-50 border border-gray-300 rounded-2xl cursor-not-allowed"
                          placeholder="0.00"
                          style={{ 
                            minHeight: '48px',
                            fontSize: '16px'
                          }}
                        />
                      </div>
                      <div className="mt-2">
                        <p className="text-xs text-gray-600">
                          Full payment amount only - partial payments are not allowed
                        </p>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-2">
                        Reference Number
                      </label>
                      <input
                        type="text"
                        value={reference}
                        onChange={(e) => setReference(e.target.value)}
                        className="w-full px-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                        placeholder="Enter reference number"
                        style={{ 
                          minHeight: '48px',
                          fontSize: '16px',
                          backdropFilter: 'blur(10px)',
                          WebkitBackdropFilter: 'blur(10px)'
                        }}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-2">
                        Notes
                      </label>
                      <textarea
                        rows={4}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        className="w-full px-4 py-3 text-base bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                        placeholder="Add any additional notes about this payment..."
                        style={{ fontSize: '16px' }}
                      />
                    </div>

                    {/* Submit Button for non-Stripe methods */}
                    <div className="pt-4">
                      <button
                        type="submit"
                        disabled={loading || !amount || parseFloat(amount) <= 0}
                        className={`w-full px-6 py-3 text-base font-medium text-white rounded-lg transition-colors ${
                          (!loading && amount && parseFloat(amount) > 0)
                            ? 'bg-blue-600 hover:bg-blue-700' 
                            : 'bg-gray-300 cursor-not-allowed'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        {loading ? (
                          <div className="flex items-center justify-center">
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                            Processing...
                          </div>
                        ) : (
                          'Record Payment'
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}